import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable from '../../../components/design/DataTable';
import automationBuilderService from '../../../services/automationBuilderService';

function createDefaultGraph() {
  const id = `n_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  return {
    nodes: [
      {
        id,
        type: 'trigger.message_received',
        position: { x: 80, y: 80 },
        data: { label: 'Mensagem recebida', config: {} }
      }
    ],
    edges: [],
    viewport: { x: 0, y: 0, zoom: 1 }
  };
}

export default function AutomationWorkflows() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('Automação');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const columns = useMemo(() => ([
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Nome' },
    { key: 'status', label: 'Status' },
    { key: 'engineWorkflowId', label: 'n8n ID' },
    { key: 'lastPublishedAt', label: 'Publicado em' },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" variant="contained" onClick={() => navigate(`/automation/${row.id}`)}>
            Editar
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => {
              setDeleteTarget(row);
              setDeleteOpen(true);
            }}
          >
            Excluir
          </Button>
        </Stack>
      ),
      align: 'right'
    }
  ]), [navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const resp = await automationBuilderService.listWorkflows();
      const data = resp?.data || resp?.data?.data || resp?.data;
      const list = resp?.data?.data || resp?.data || resp?.data?.workflows || resp?.data?.items || resp?.data;
      const normalized = Array.isArray(resp?.data?.data) ? resp.data.data : Array.isArray(resp?.data) ? resp.data : Array.isArray(list) ? list : Array.isArray(data) ? data : [];
      setRows(normalized);
    } catch (e) {
      toast.error(e?.message || 'Falha ao carregar workflows');
      if (e?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    try {
      const resp = await automationBuilderService.createWorkflow({ name, graph: createDefaultGraph() });
      const id = resp?.data?.id || resp?.data?.data?.id || resp?.id || resp?.data?.data?.data?.id;
      if (!id) throw new Error('Falha ao criar workflow');
      setOpen(false);
      navigate(`/automation/${id}`);
    } catch (e) {
      toast.error(e?.message || 'Falha ao criar workflow');
    }
  };

  const confirmDelete = async () => {
    const id = Number(deleteTarget?.id || 0);
    if (!id) return;
    try {
      await automationBuilderService.deleteWorkflow(id);
      toast.success('Fluxo excluído');
      setDeleteOpen(false);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      if (e?.status === 404) {
        toast.info('Fluxo não encontrado (provavelmente já foi removido)');
        setDeleteOpen(false);
        setDeleteTarget(null);
        await load().catch(() => {});
        return;
      }
      toast.error(e?.message || 'Falha ao excluir fluxo');
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Automação</Typography>
          <Typography variant="body2" color="text.secondary">Builder visual (React Flow) com publicação no n8n</Typography>
        </Box>
        <Button variant="contained" onClick={() => setOpen(true)}>Novo fluxo</Button>
      </Stack>

      <DataTable
        columns={columns}
        rows={rows}
        getKey={(r) => String(r.id)}
      />

      {loading && (
        <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">Carregando...</Typography>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Novo fluxo</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={create}>Criar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Excluir fluxo</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Excluir o fluxo "{deleteTarget?.name || ''}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Excluir</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
